// PatientsList.jsx

import React, { useState, useEffect, useRef, useContext } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import DeleteConfirmation from "../deleteConfirmation";
import Table from "../Theme/Table.js";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import EditPatient from "./EditPatient";
import Badge from "@mui/material/Badge";
import Spinner from "../Spinner";
import { RoleContext } from "../RoleContext";
import { handleExportClickDriver } from "../exportDriverFile";
import { Button } from "react-bootstrap";
import { Checkbox, Modal } from "antd";
import { getGenderText, getIncidentTypeText, getTreatmentTypeText } from "../enum.js";
import ActionButtons from "../Trip/TripsActionButton.js";


import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';


const PatientsList = () => {
  const roleData = useContext(RoleContext);
  const role = JSON.parse(roleData);

  const navigate = useNavigate();


  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  const [id, setId] = useState(null);
  const [data, setData] = useState(null);

  const [deleteMessage, setDeleteMessage] = useState(null);
  const [displayConfirmationModal, setDisplayConfirmationModal] = useState(false);

  const [displayEditModal, setDisplayEditModal] = useState(false);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);

  const printableRef = useRef(null);

  const notifySuccess = (msg) =>
    toast.success(msg, {
      position: "top-right",
      autoClose: 1000,
    });

  const notifyError = (msg) =>
    toast.error(msg || "Something went wrong...", {
      position: "top-right",
      autoClose: 1000,
    });

  const getPatients = async () => {
    try {
      const response = await axios.get("/patients");
      setPatients(response.data);
      setLoading(false);
      notifySuccess("Patients list loaded.");
    } catch (error) {
      setLoading(false);
      notifyError();
    }
  };

  useEffect(() => {
    getPatients();
  }, []);

  const showDeleteModal = (patientId) => {
    setId(patientId);
    setDeleteMessage(`Are you sure you want to delete this patient?`);
    setDisplayConfirmationModal(true);
  };

  const hideConfirmationModal = () => {
    setDisplayConfirmationModal(false);
  };

  const submitDelete = async (patientId) => {
    try {
      await axios.delete(`/patients/${patientId}`);
      notifySuccess("Patient deleted successfully.");
      setDisplayConfirmationModal(false);
      getPatients();
    } catch (error) {
      notifyError("Failed to delete patient");
    }
  };

  const showEditModal = async (patientId) => {
    setId(patientId);
    try {
      const response = await axios.get(`/patients/${patientId}`);
      setData(response.data);
      setDisplayEditModal(true);
    } catch (error) {
      notifyError("Failed to fetch patient data.");
    }
  };

  const hideEditModal = () => {
    setDisplayEditModal(false);
  };

  const columns = React.useMemo(
  () => [
    { Header: "ID", accessor: "id" },
    {
      Header: "Name",
      columns: [
        { Header: "First Name", accessor: "firstName" },
        { Header: "Middle Name", accessor: "middleName" },
        { Header: "Last Name", accessor: "lastName" },
      ],
    },
    {
      Header: "Personal Info",
      columns: [
        { Header: "Age", accessor: "age" },
        {
          Header: "Gender",
          accessor: "gender",
          Cell: (row) => getGenderText(row.cell.row.original.gender),
        },
        { Header: "Phone No", accessor: "patientPhoneNo" },
        { Header: "Patient ID", accessor: "patientID" },
      ],
    },
    {
      Header: "Medical Info",
      columns: [
        {
          Header: "Incident Type",
          accessor: "incidentType",
          Cell: (row) => getIncidentTypeText(row.cell.row.original.incidentType)
        },
        { Header: "Incident Type Other", accessor: "incidentTypeOther" },
        {
          Header: "First Responder Treatments",
          accessor: "firstResponderTreatments",
          Cell: (row) => {
            const treatment = row.cell.row.original.firstResponderTreatments;
            return treatment ? getTreatmentTypeText(treatment) : "N/A";
          }
        },
        { Header: "First Responder Other Treatment", accessor: "firstResponderOtherTreatment" },
        {
          Header: "Ambulance Treatments",
          accessor: "ambulanceTreatments",
          Cell: (row) => {
            const treatment = row.cell.row.original.ambulanceTreatments;
            return treatment ? getTreatmentTypeText(treatment) : "N/A";
          }
        },
        { Header: "Ambulance Other Treatment", accessor: "ambulanceOtherTreatment" },
      ],
    },
    {
      Header: "System Info",
      columns: [
        {
          Header: "Status",
          accessor: "status",
          disableFilters: true,
          Cell: (row) => {
            const status = row.cell.row.original.status || "Active";
            return (
              <Chip 
                label={status} 
                color={status === "Active" ? "success" : "warning"}
                variant="filled"
                sx={{
                  fontWeight: 'bold',
                  borderRadius: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  boxShadow: status === "Active" 
                    ? '0 2px 4px rgba(0,255,0,0.2)' 
                    : '0 2px 4px rgba(255,165,0,0.2)'
                }}
              />
            );
          },
        },
        {
          Header: "Action",
          accessor: "action",
          disableFilters: true,
          Cell: (row) => (
            <div style={{ 
              display: "flex", 
              gap: "15px", 
              alignItems: 'center' 
            }}>
              {/* {role.filter(e => e.perm_name === "update_patients").length > 0 && (
                <Tooltip title="Edit Patient">
                  <span>
                    <EditIcon 
                      color="primary" 
                      sx={{ 
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'scale(1.2)',
                          color: '#1e40af'
                        }
                      }}
                      onClick={() => showEditModal(row.cell.row.original.id)}
                    />
                  </span>
                </Tooltip>
              )} */}
              {role.filter(e => e.perm_name === "list_patients").length > 0 && (
                <Tooltip title="View Patient Details">
                  <span>
                    <VisibilityIcon 
                      color="success" 
                      sx={{ 
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'scale(1.2)',
                          color: '#16a34a'
                        }
                      }}
                      onClick={() => navigate(`/viewPatient/${row.cell.row.original.id}`)}
                    />
                  </span>
                </Tooltip>
              )}
              {/* {role.filter(e => e.perm_name === "delete_patient").length > 0 && (
                <Tooltip title="Delete Patient">
                  <span>
                    <DeleteIcon 
                      color="error" 
                      sx={{ 
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'scale(1.2)',
                          color: '#ff0000'
                        }
                      }}
                      onClick={() => showDeleteModal(row.cell.row.original.id)}
                    />
                  </span>
                </Tooltip>
              )} */}
            </div>
          ),
        },
      ],
    },
  ],
  [role]
);
  const columns_expo = React.useMemo(
    () => [
      { Header: "ID", accessor: "id" },
      { Header: "First Name", accessor: "firstName" },
      { Header: "Middle Name", accessor: "middleName" },
      { Header: "Last Name", accessor: "lastName" },
      { Header: "Age", accessor: "age" },
      { Header: "Gender", accessor: "gender" },
      { Header: "Phone No", accessor: "patientPhoneNo" },
      { Header: "Patient ID", accessor: "patientID" },
      { Header: "Incident Type", accessor: "incidentType" },
      { Header: "Incident Type Other", accessor: "incidentTypeOther" },
      { Header: "First Responder Treatments", accessor: "firstResponderTreatments" },
      { Header: "First Responder Other Treatment", accessor: "firstResponderOtherTreatment" },
      { Header: "Ambulance Treatments", accessor: "ambulanceTreatments" },
      { Header: "Ambulance Other Treatment", accessor: "ambulanceOtherTreatment" },
    ],
    []
  );

  useEffect(() => {
    setSelectedColumns(columns_expo.map((col) => col.Header));
  }, [columns_expo]);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleOkModal = () => {
    setIsModalVisible(false);
    handleExportClickDriver(
      patients,
      columns_expo,
      selectedColumns,
      "Patients",
      "patients.pdf"
    );
  };

  function handleColumnToggle(columnHeader) {
    setSelectedColumns((prevSelectedColumns) => {
      if (prevSelectedColumns.includes(columnHeader)) {
        return prevSelectedColumns.filter((col) => col !== columnHeader);
      } else {
        return [...prevSelectedColumns, columnHeader];
      }
    });
  }

  return (
    <div className="content-wrappers" style={{ marginTop: '-40px' }}>
    <div className="content-wrappers">
      <br />
      <br />
      <ToastContainer />

      <section className="content" ref={printableRef}>
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">

              <div className="card">

                <ActionButtons title="Patients" showModal={showModal}/>
                  <Modal
                
                    title="Select Columns to Export"
                    visible={isModalVisible}
                    onOk={handleOkModal}
                    onCancel={handleCancel}
                    footer={[
                      <Button key="cancel" onClick={handleCancel}>
                        Cancel
                      </Button>,
                      <Button
                        style={{ marginLeft: "10px" }}
                        key="ok"
                        type="primary"
                        onClick={handleOkModal}
                      >
                        OK
                      </Button>,
                    ]}
                  >
                    <div style={{ marginBottom: "20px" }}>
                      {columns_expo.map((column) => (
                        <div
                          key={column.Header}
                          style={{ marginBottom: "10px" }}
                        >
                          <Checkbox
                            checked={selectedColumns.includes(column.Header)}
                            onChange={() => handleColumnToggle(column.Header)}
                            style={{ marginRight: "10px", fontSize: "16px" }}
                          >
                            {column.Header}
                          </Checkbox>
                        </div>
                      ))}
                    </div>
                  </Modal>

                <div className="card-body">
                  {loading && <Spinner />}
                  {!loading && (
                    <Table
                      columns={columns}
                      data={patients}
                      edit="patient"
                      detail="viewPatient"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <DeleteConfirmation
        showModal={displayConfirmationModal}
        confirmModal={submitDelete}
        hideModal={hideConfirmationModal}
        id={id}
        message={deleteMessage}
      />

      {displayEditModal && (
        <EditPatient
          id={id}
          data={data}
          getPatients={getPatients}
          showModal={displayEditModal}
          hideModal={hideEditModal}
        />
      )}
    </div>
    </div>
  );
};

export default PatientsList;
